import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { TemplateCard } from '@/components/jira/service-management/templates/template-card';
import { Template } from '@/content/templates/template.types';
import { Row } from '@/libraries/wpl/foundations/src/grid';

const fontCharlieDisplay = `"Charlie Display", "Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`;
const fontCharlieText = `"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`;

const backgroundStyles = css({
	backgroundImage: `linear-gradient(180deg, #4C9AFF -51.72%, #DEEBFF 124.8%)`,
	backgroundRepeat: 'no-repeat',
	overflowX: 0,
	overflowY: 0,
});

const containerStyles = css({
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
	paddingBlockEnd: '16px',
	paddingBlockStart: '16px',
	maxWidth: '1200px',
	[media.above.sm]: {
		paddingBlockEnd: '96px',
		paddingBlockStart: '96px',
	},
});

const titleStyles = css({
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
	width: '100%',
	paddingInlineStart: '16px',
	paddingInlineEnd: '16px',
	paddingBlockEnd: '32px',
	[media.above.sm]: {
		width: '83.333%',
		textAlign: 'center',
		paddingBlockEnd: '64px',
	},
	[media.above.md]: {
		width: '66.666%',
		paddingBlockEnd: '64px',
	},
});

const h1Styles = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#253858 !important',
	lineHeight: '1.18181818',
	fontFamily: fontCharlieDisplay,
	fontWeight: '700 !important',
	marginBlockStart: '0',
	marginBlockEnd: '16px',
	fontSize: '2rem !important',
	[media.above.sm]: {
		fontSize: '2.75rem !important',
	},
	[media.above.md]: {
		fontSize: '3rem !important',
	},
});

const pStyles = css({
	color: token('color.text'),
	fontFamily: fontCharlieText,
});

const gridContainerStyles = css({
	width: '100%',
	paddingInlineStart: '16px !important',
	paddingInlineEnd: '16px !important',
	// Override the default padding of the row layout
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.row-layout': {
		paddingInlineStart: '0 !important',
		paddingInlineEnd: '0 !important',
	},
});

export const TemplatesGrid = ({ templates }: { templates: Template[] }) => {
	const templateGridRows = () => {
		const rows = [];
		for (let i = 0; i < templates.length; i += 3) {
			rows.push(templates.slice(i, i + 3));
		}
		return rows;
	};

	return (
		<div css={backgroundStyles}>
			<div css={containerStyles}>
				<div css={titleStyles}>
					<h1 css={h1Styles}>
						<FormattedMessage
							id="jsm.templates.page.title"
							defaultMessage={'Service management templates'}
						/>
					</h1>
					<p css={pStyles}>
						<FormattedMessage
							id="jsm.templates.page.subtitle"
							defaultMessage={
								'Empower any team to get started with a service experience in Jira Service Management with pre-configured request types, workflows, and automations.'
							}
						/>
					</p>
				</div>
				<div data-testid="templates-grid" css={gridContainerStyles}>
					{templateGridRows().map((row, index) => (
						<Row key={`template-grid-row-${index}`} layout="4-4-4">
							{row.map((template) => (
								<TemplateCard key={template.urlSlug} template={template} />
							))}
						</Row>
					))}
				</div>
			</div>
		</div>
	);
};
